/**
 * Catches a lazy chunk that failed to load (see `staleChunk.ts`) so the reader
 * never gets a blank screen: the first failure reloads the page, a second one
 * in the same session shows a small Reload card. Any other error is rethrown
 * to the boundary above, untouched.
 */
import { Component, type ReactNode } from "react";
import {
  isStaleChunkError,
  isStaleChunkReloadPending,
  reloadByHand,
  reloadForStaleChunk,
} from "./staleChunk";

interface Props {
  children: ReactNode;
}

interface State {
  error: unknown;
  /** The error was a stale chunk and the page could not reload for it. */
  showCard: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null, showCard: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  componentDidCatch(error: unknown): void {
    if (!isStaleChunkError(error)) return;
    if (!reloadForStaleChunk()) this.setState({ showCard: true });
  }

  render(): ReactNode {
    const { error, showCard } = this.state;
    if (error === null) return this.props.children;
    // The page is reloading: whatever threw on the way (once the preload error is
    // handled, Vite's import resolves to undefined and the `.then` throws) is moot.
    if (isStaleChunkReloadPending()) return null;
    if (!isStaleChunkError(error)) throw error;
    if (!showCard) return null;
    return <ChunkReloadCard />;
  }
}

export function ChunkReloadCard() {
  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 10000,
        maxWidth: "min(320px, calc(100vw - 32px))",
        boxSizing: "border-box",
        padding: "var(--chrome-spacing-xl)",
        borderRadius: "var(--chrome-radius-lg)",
        background: "var(--chrome-color-base)",
        color: "var(--chrome-color-ink)",
        boxShadow: "var(--chrome-shadow-lifted)",
        fontFamily: "var(--chrome-font-ui)",
        textAlign: "center",
      }}
    >
      <p style={{ margin: "0 0 var(--chrome-spacing-lg)", fontSize: 17, lineHeight: 1.4 }}>
        Something new is ready. Reload to see it.
      </p>
      <button
        type="button"
        onClick={() => reloadByHand()}
        style={{
          minHeight: 44,
          minWidth: 120,
          padding: "0 var(--chrome-spacing-xl)",
          border: "none",
          borderRadius: "var(--chrome-radius-pill)",
          background: "var(--chrome-color-sun)",
          color: "var(--chrome-color-ink)",
          fontFamily: "inherit",
          fontSize: 17,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        Reload
      </button>
    </div>
  );
}
